import { ChangeEvent, ReactNode, useCallback, useRef, useState } from 'react'

import { Header2, PageTemplateContent, StatusMessage } from 'app/common/templates-next/page-template'
import ControlsBar from 'app/common/ui-next/controls-bar'
import PlainButton from 'app/common/ui-next/plain-button'
import { PromptRouter3Custom } from 'app/components/common/Prompt'
import useScrollToError from 'app/common/hooks/useScrollToError'
import usePopup from 'app/common/hooks/usePopup'
import { WithdrawToParentType } from 'app/dataTypes/cafeteria/compensation/withdraw/toParent'
import Sticky from 'app/common/templates-next/sticky'

import useCallbackRef from 'app/common/hooks/useCallbackRef'
import { ChildControls } from './ChildControls'
import { ParentControls } from './ParentControls'
import { BalanceInfo } from '../BalanceInfo/BalanceInfo'
import { ControlsRowItem } from './templates/ControlsRowItem'
import { ControlsRow } from './templates/ControlsRow'
import { BankControls, BankControlsHandle } from './BankControls'
import { WithdrawDraftDownload } from '../_common/WithdrawDraftDownload'
import { ControlValues, getControlValues, useErrors } from './controls'

import styles from './WithdrawToParent.scss'


const formID = 'withdrawToParent'

type Props = {
  withdrawID: string | null
  withdrawData: WithdrawToParentType | null
  loading: boolean
  returnToRoot: () => void
  onNewDraft: (values: ControlValues) => Promise<string | null>
  onUpdateDraft: (values: ControlValues) => Promise<boolean>
  onSendDraft: (withdrawID: string) => Promise<boolean>
  onDownloadDraft: () => Promise<boolean>
  route: any
  errorExplanation: string
  agreement: ReactNode
}

export const WithdrawToParentForm = ({
  withdrawID,
  withdrawData,
  loading,
  returnToRoot,
  onNewDraft,
  onUpdateDraft,
  onSendDraft,
  onDownloadDraft,
  route,
  errorExplanation,
  agreement,
}: Props) => {
  const onNewDraftRef = useCallbackRef(onNewDraft)
  const onUpdateDraftRef = useCallbackRef(onUpdateDraft)
  const onSendDraftRef = useCallbackRef(onSendDraft)

  const bankControlsRef = useRef<BankControlsHandle>(null)

  const [pending, setPending] = useState(false)

  const scrollToError = useScrollToError()
  const { errors, validateControlValues, resetErrors } = useErrors(scrollToError)

  const popup = usePopup()

  const [modified, setModified] = useState(false)
  const handleModified = useCallback((modified_: boolean) => {
    if (!modified_) resetErrors()
    setModified(modified_)
  }, [resetErrors])

  const handleSubmit = useCallback(async (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formEvent = e.target
    const { id } = formEvent

    if (id !== formID) return

    const controlValues = getControlValues(formEvent)

    setPending(true)
    bankControlsRef.current?.updateBankDetails()
    if (!withdrawID) {
      handleModified(false)
      await onNewDraftRef.current?.(controlValues)
    }
    if (withdrawID && modified) {
      handleModified(false)
      const res = await onUpdateDraftRef.current?.(controlValues)
      if (res) popup('Данные успешно обновлены')
    }
    if (withdrawID && !modified && validateControlValues(controlValues)) {
      const res = await onSendDraftRef.current?.(withdrawID)
      if (res) popup('Заявка успешно отправлена')
      returnToRoot()
    }
    setPending(false)
  }, [
    bankControlsRef,
    withdrawID,
    validateControlValues,
    handleModified,
    modified,
    returnToRoot,
    popup,
    onNewDraftRef,
    onUpdateDraftRef,
    onSendDraftRef,
  ])

  const lockControls = pending || !withdrawData
  const lockSubmit = loading || lockControls

  return (
    <form onSubmit={handleSubmit} id={formID}>
      <Sticky>
        {(ref, _, shadow) => (
          <PageTemplateContent reference={ref} shadowBottom={shadow}>
            <Header2 skipMargin>
              {'Заявление о выводе компенсации на расчетный счет родителя'}
            </Header2>
          </PageTemplateContent>
        )}
      </Sticky>

      {!loading
        && !withdrawData
        && <PageTemplateContent skipPadding>
          <StatusMessage error>
            {`Cоздание заявки невозможно${errorExplanation ? `. ${errorExplanation}` : ''}`}
          </StatusMessage>
        </PageTemplateContent>}

      <PageTemplateContent>
        <ChildControls
          studentName={withdrawData?.studentFullname || ''}
          classTitle={withdrawData?.classTitle || ''}
          schoolTitle={withdrawData?.schoolTitle || ''}
          loading={loading}
        />
      </PageTemplateContent>

      <PageTemplateContent>
        <ControlsRow>
          <ControlsRowItem width="50%">
            <BalanceInfo
              loading={loading}
              balance={withdrawData?.availableWithdrawValue || 0}
            />
          </ControlsRowItem>
        </ControlsRow>
      </PageTemplateContent>

      <PageTemplateContent>
        <ParentControls
          loading={loading}
          disabled={lockControls}
          parentName={withdrawData?.parentFullname || ''}
          parentPhone={withdrawData?.parentPhone || ''}
          parentAddress={withdrawData?.parentAddress || ''}
          onModified={handleModified}
          errors={errors}
        />
      </PageTemplateContent>

      <PageTemplateContent>
        <BankControls
          ref={bankControlsRef}
          loading={loading}
          disabled={lockControls}
          onModified={handleModified}
        />
      </PageTemplateContent>

      <Sticky stickyBottom>
        {(ref, _, shadow) => (
          <PageTemplateContent reference={ref} shadowTop={shadow}>
            {withdrawID
              && <WithdrawDraftDownload
                onClick={onDownloadDraft}
                className={styles.withdrawLink}
              />}

            <ControlsBar right>
              {agreement}
              <PlainButton onClick={returnToRoot} outline>
                {'Отмена'}
              </PlainButton>
              <PlainButton type="submit" disabled={lockSubmit}>
                {!withdrawID || modified ? 'Сохранить' : 'Отправить'}
              </PlainButton>
            </ControlsBar>
          </PageTemplateContent>
        )}
      </Sticky>

      <PromptRouter3Custom
        route={route}
        when={modified}
        message="Вы не сохранили изменения, перейти в любом случае?"
      />
    </form>
  )
}
